import {
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
  Download as DownloadIcon,
  VpnKey as KeyIcon,
  PhoneAndroid as PhoneIcon,
  MenuBook as ResourcesIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Visibility,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HardwareIdSubmission from "../components/user/HardwareIdSubmission";
import { AuthContext } from "../contexts/AuthContext";
import { getProducts } from "../services/productService";
import { userProductLicenseService } from "../services/userProductLicenseService";
import { userProductService } from "../services/userProductService";
import { themedButtonStyle } from "../styles/themeStyles";
import { isLicenseServerAssignment } from "../utils/licenseServerUtils";
import {
  calculateLicenseStatus,
  getDaysUntilExpiry,
} from "../utils/licenseStatusUtils";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [myLicenses, setMyLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [licenseAlerts, setLicenseAlerts] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch latest products
      const productsData = await getProducts();
      setProducts(productsData);

      // Fetch user's assigned products
      let fetchedMyProducts = [];
      try {
        const myProductsData = await userProductService.getMyProducts();
        fetchedMyProducts = myProductsData.products || [];
        setMyProducts(fetchedMyProducts);
      } catch (err) {
        console.error("Error fetching my products:", err);
      }

      // Product ids for which this user's assignment is a license-server
      // assignment (they host/issue licenses rather than consume one) — all
      // license-related UI must be suppressed for these products.
      const licenseServerProductIds = new Set(
        fetchedMyProducts
          .filter((p) => isLicenseServerAssignment(p))
          .map((p) => p.id),
      );

      // Fetch user's licenses
      try {
        const licensesData = await userProductLicenseService.getMyLicenses();
        const licenses = (licensesData.productLicenses || []).filter(
          (license) => !licenseServerProductIds.has(license.product_id),
        );
        setMyLicenses(licenses);

        // Check for expiring and expired licenses using centralized logic
        const expiring = licenses
          .filter((license) => license && license.license_key) // Only check licenses that have license keys
          .filter((license) => {
            const status = calculateLicenseStatus(license);
            return status === "expiring_soon";
          });

        const expired = licenses
          .filter((license) => license && license.license_key) // Only check licenses that have license keys
          .filter((license) => {
            const status = calculateLicenseStatus(license);
            return status === "expired";
          });

        // Create license alerts
        const alerts = [];
        if (expired.length > 0) {
          alerts.push({
            severity: "error",
            message: `${expired.length} license${
              expired.length > 1 ? "s have" : " has"
            } expired`,
            licenses: expired,
          });
        }
        if (expiring.length > 0) {
          alerts.push({
            severity: "warning",
            message: `${expiring.length} license${
              expiring.length > 1 ? "s are" : " is"
            } expiring within 10 days`,
            licenses: expiring,
          });
        }

        setLicenseAlerts(alerts);
      } catch (err) {
        console.error("Error fetching licenses:", err);
      }

      setError("");
    } catch (err) {
      setError("Failed to load dashboard data. Please try again later.");
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProduct = (productId) => {
    navigate(`/products/${productId}`);
  };

  const handleViewAllProducts = () => {
    navigate("/products");
  };

  const getLatestVersionForProduct = (product) => {
    return product.ProductVersions?.find((v) => v.is_latest);
  };

  // The catalog product list (from getProducts) doesn't carry the
  // per-assignment through-flag, so cross-reference against myProducts
  // (from getMyProducts) to find whether this user's assignment for a given
  // product is a license-server assignment.
  const isLicenseServerForProduct = (productId) => {
    const myProduct = myProducts.find((p) => p.id === productId);
    return isLicenseServerAssignment(myProduct);
  };

  const getLicenseForProduct = (productId) => {
    // Find the license data from myLicenses using the updated structure
    const license = myLicenses.find(
      (license) => license.product_id === productId,
    );

    if (!license) {
      return null;
    }

    // Return license regardless of active status - let the UI handle status display
    if (license && license.license_key) {
      return license;
    }
    return null;
  };

  // Import the centralized license status utilities
  const isLicenseExpiring = (license) => {
    if (!license || !license.license_key) return false;
    // Check if license is inactive
    if (license.is_active === false) return false;

    const status = calculateLicenseStatus(license);
    return status === "expiring_soon";
  };

  const isLicenseExpired = (license) => {
    if (!license || !license.license_key) return false;
    const status = calculateLicenseStatus(license);
    return status === "expired";
  };

  const isLicenseInactive = (license) => {
    if (!license) return false;
    return license.is_active === false;
  };

  // Use the centralized getDaysUntilExpiry function from utils
  const getDaysUntilExpiryLocal = (license) => {
    return getDaysUntilExpiry(license);
  };

  // Generate placeholder image based on product name
  const getProductImage = (productName) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];
    const color = colors[productName.charCodeAt(0) % colors.length];
    return color;
  };

  const renderLatestProducts = () => {
    if (products.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No products available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for new products
          </Typography>
        </Box>
      );
    }

    return (
      <Grid
        container
        spacing={{ xs: 2, sm: 2.5, md: 3 }}
        sx={{ alignItems: "stretch" }}
      >
        {products.map((product, index) => {
          const latestVersion = getLatestVersionForProduct(product);
          const productColor = getProductImage(product.name);
          const isLicenseServer = isLicenseServerForProduct(product.id);
          const showLicenseUi = product.requires_license && !isLicenseServer;
          const license = showLicenseUi
            ? getLicenseForProduct(product.id)
            : null;
          const isExpiring = license && isLicenseExpiring(license);
          const isExpired = license && isLicenseExpired(license);
          const isInactive = license && isLicenseInactive(license);
          const daysUntilExpiry = license ? getDaysUntilExpiry(license) : null;

          // Determine the overall license status
          let licenseStatus = "NO LICENSE";
          let licenseColor = "error";
          let licenseIcon = <KeyIcon />;

          if (license) {
            if (isInactive) {
              licenseStatus = "INACTIVE";
              licenseColor = "default";
              licenseIcon = <WarningIcon />;
            } else if (isExpired) {
              licenseStatus = "EXPIRED";
              licenseColor = "error";
              licenseIcon = <WarningIcon />;
            } else if (isExpiring) {
              licenseStatus = `${daysUntilExpiry}d left`;
              licenseColor = "warning";
              licenseIcon = <ScheduleIcon />;
            } else {
              licenseStatus = "ACTIVE";
              licenseColor = "success";
              licenseIcon = <CheckCircleIcon />;
            }
          }

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={product.id}
              sx={{ width: "100%" }}
            >
              <Card
                elevation={1}
                variant="outlined"
                className="dashboard-card" // Add CSS class
                sx={{
                  position: "relative",
                  height: { xs: 540, sm: 560, md: 580 }, // Increased height to accommodate layout
                  width: "100%",
                  minWidth: { xs: 280, sm: 300, md: 300 },
                  display: "flex",
                  flexDirection: "column",
                  borderColor: "border.secondary",
                  ...(isExpired && {
                    border: 2,
                    borderColor: "error.main",
                  }),
                  ...(isExpiring && {
                    border: 2,
                    borderColor: "warning.main",
                  }),
                }}
              >
                {/* License Status Badge */}
                {showLicenseUi && (
                  <Chip
                    icon={licenseIcon}
                    label={licenseStatus}
                    color={licenseColor}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 8,
                      zIndex: 2,
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                    }}
                  />
                )}

                {/* Product Image/Avatar */}
                <Paper
                  elevation={0}
                  sx={{
                    position: "relative",
                    height: "100%",
                    width: "100%",
                    border: "none",
                    background: product.banner_image
                      ? "none"
                      : `linear-gradient(135deg, ${productColor}, ${alpha(
                          productColor,
                          0.7,
                        )})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {product.banner_image ? (
                    <img
                      src={product.banner_image}
                      alt={`${product.name} banner`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <>
                      <Avatar
                        sx={{
                          width: "100%",
                          height: "100%",
                          fontSize: "2rem",
                          fontWeight: "bold",
                          bgcolor: "rgba(255,255,255,0.2)",
                          color: "common.white",
                        }}
                      >
                        <SecurityIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Typography
                        align="center"
                        sx={{
                          position: "absolute",
                          alignSelf: "center",
                          top: 0,
                          bottom: 0,
                          left: 0,
                          right: 0,
                          backgroundColor: "rgba(0,0,0,0.25)",
                          padding: "20px",
                          fontSize: "20px",
                          color: "#000",
                          fontWeight: "bold",
                        }}
                      >
                        No preview image available for this product
                      </Typography>
                    </>
                  )}

                  {/* Version chip overlay */}
                  {latestVersion && (
                    <Chip
                      label={`v${latestVersion.version}`}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        bgcolor: "rgba(255,255,255,0.9)",
                        color: productColor,
                        fontWeight: "bold",
                        borderRadius: 2,
                      }}
                    />
                  )}

                  {/* New badge for recent products */}
                  {/* {index === 0 && (
                    <Chip
                      label="NEW"
                      size="small"
                      icon={<Star sx={{ fontSize: 16 }} />}
                      sx={{
                        position: "absolute",
                        top: product.requires_license ? 56 : 16,
                        left: product.requires_license ? 8 : 16,
                        bgcolor: (theme) => theme.palette.success.main,
                        color: "white",
                        fontWeight: "bold",
                        borderRadius: 2,
                      }}
                    />
                  )} */}
                </Paper>

                <CardContent
                  className="card-content" // Add CSS class
                  sx={{
                    flexGrow: 1,
                    p: { xs: 2, sm: 2.5, md: 3 },
                    pb: { xs: 1, sm: 1.5, md: 2 },
                    display: "flex",
                    flexDirection: "column",
                    height: { xs: 200, sm: 210, md: 220 },
                    justifyContent: "flex-start", // Changed from space-between to flex-start
                    position: "relative", // Add relative positioning
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      lineHeight: 1.3,
                      height: "auto",
                      minHeight: "1.5em",
                      mb: { xs: 0.8, sm: 1, md: 1 },
                      fontSize: { xs: "1.1rem", sm: "1.15rem", md: "1.25rem" },
                    }}
                  >
                    {product.name}
                  </Typography>

                  <Box sx={{ flex: 1, pb: { xs: 7, sm: 8, md: 9 } }}>
                    {" "}
                    {/* Add padding bottom to make space for chips */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        display: "-webkit-box",
                        WebkitLineClamp: { xs: 2, sm: 3, md: 3 },
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        mb: { xs: 1, sm: 1.2, md: 1.5 },
                        height: { xs: "3.2em", sm: "4.8em", md: "4.8em" },
                        fontSize: {
                          xs: "0.85rem",
                          sm: "0.875rem",
                          md: "0.875rem",
                        },
                      }}
                    >
                      {product.description ||
                        "No description available for this product."}
                    </Typography>
                    {/* License Information */}
                    {showLicenseUi && license && (
                      <Box sx={{ mb: { xs: 1, sm: 1.2, md: 1.5 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{
                            fontSize: {
                              xs: "0.65rem",
                              sm: "0.7rem",
                              md: "0.7rem",
                            },
                            lineHeight: 1.2,
                            mb: { xs: 0.5, sm: 0.7, md: 1 },
                          }}
                        >
                          License expires:{" "}
                          {new Date(license.expiry_date).toLocaleDateString()}
                        </Typography>
                        {isExpiring && (
                          <LinearProgress
                            variant="determinate"
                            value={Math.max(
                              0,
                              Math.min(100, (daysUntilExpiry / 10) * 100),
                            )}
                            color={daysUntilExpiry <= 3 ? "error" : "warning"}
                            sx={{ mt: 0.25, height: 3, borderRadius: 2 }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Chips Section - Always visible and properly spaced */}
                  <Box
                    className="card-chips" // Add CSS class
                    sx={{
                      position: "absolute", // Position absolutely within CardContent
                      bottom: 0, // Stick to bottom of CardContent
                      left: { xs: 2, sm: 2.5, md: 3 }, // Match content padding
                      right: { xs: 2, sm: 2.5, md: 3 }, // Match content padding
                      pb: { xs: 1, sm: 1.5, md: 2 }, // Match content bottom padding
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={{ xs: 0.8, sm: 1, md: 1 }}
                      sx={{ flexWrap: "wrap", gap: 0.5 }}
                    >
                      <Chip
                        label="Forensic Tool"
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ fontWeight: 500 }}
                      />
                      {showLicenseUi && (
                        <Chip
                          label="Licensed"
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </Stack>
                  </Box>
                </CardContent>

                <CardActions
                  className="card-actions" // Add CSS class
                  sx={{
                    p: { xs: 1.5, sm: 1.7, md: 2 }, // Increased padding
                    pt: { xs: 0.5, sm: 0.7, md: 1 }, // Reduced top padding
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 1, sm: 1.2, md: 1.5 }, // Increased gap
                    mt: 0, // Remove auto margin
                    height: { xs: 100, sm: 105, md: 110 }, // Reduced height since chips moved up
                    flexDirection: "row",
                  }}
                >
                  {/* Prominent Download Button */}
                  {isLicenseServer ? (
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handleViewProduct(product.id)}
                      sx={{
                        py: { xs: 1, sm: 1.2, md: 1.5 }, // Adjusted padding
                        fontSize: {
                          xs: "0.8rem",
                          sm: "0.85rem",
                          md: "0.875rem",
                        }, // Slightly smaller font
                        fontWeight: "600",
                      }}
                    >
                      View Available Downloads
                    </Button>
                  ) : (
                    latestVersion && (
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                        onClick={() =>
                          navigate(`/download/${latestVersion.id}`)
                        }
                        sx={{
                          py: { xs: 1, sm: 1.2, md: 1.5 }, // Adjusted padding
                          fontSize: {
                            xs: "0.8rem",
                            sm: "0.85rem",
                            md: "0.875rem",
                          }, // Slightly smaller font
                          fontWeight: "600",
                        }}
                      >
                        Download v{latestVersion.version}
                      </Button>
                    )
                  )}

                  {/* Secondary Actions Row */}
                  <Stack
                    direction="row"
                    spacing={{ xs: 0.8, sm: 1, md: 1 }}
                    sx={{ height: "100%" }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => handleViewProduct(product.id)}
                      sx={{
                        ...themedButtonStyle(),
                        py: { xs: 0.6, sm: 0.7, md: 0.75 }, // Reduced padding
                        fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" }, // Smaller font
                      }}
                    >
                      View features, specifications and other details
                    </Button>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Container
        sx={{
          mt: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 4, sm: 6, md: 8 },
          px: { xs: 1, sm: 2, md: 3 },
          textAlign: "center",
        }}
      >
        <CircularProgress size={{ xs: 40, sm: 50, md: 60 }} />
        <Typography
          variant="body1"
          sx={{
            mt: { xs: 1.5, sm: 2, md: 2 },
            fontSize: { xs: "0.9rem", sm: "1rem", md: "1rem" },
          }}
        >
          Loading dashboard...
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: { xs: 2, sm: 3, md: 4 },
        mb: { xs: 4, sm: 6, md: 8 },
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Typography
        variant="body1"
        paragraph
        align="center"
        sx={{
          color: "text.primary",
          fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" },
          mb: { xs: 2, sm: 2.5, md: 3 },
          lineHeight: 1.6,
        }}
      >
        {`Welcome back, ${currentUser.email}! Access your forensic intelligence tools and resources.`}
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* License Alerts */}
      {licenseAlerts.map((alert, index) => (
        <Alert
          key={index}
          severity={alert.severity}
          sx={{
            mb: { xs: 1.5, sm: 2, md: 2 },
            mt: { xs: 1, sm: 1.5, md: 2 },
            borderRadius: 2,
            border: "2px solid",
            borderColor:
              alert.severity === "error" ? "error.main" : "warning.main",
            bgcolor:
              alert.severity === "error"
                ? "rgba(244, 67, 54, 0.04)"
                : "rgba(255, 152, 0, 0.04)",
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
            p: { xs: 1.5, sm: 2, md: 2 },
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setTabValue(1)}
              sx={{
                fontWeight: "bold",
                textTransform: "none",
                px: 2,
                py: 0.5,
              }}
            >
              VIEW DETAILS
            </Button>
          }
        >
          <Box>
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
              {alert.message}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {alert.licenses.slice(0, 3).map((license) => {
                // Find the product name for this license
                const product = products.find(
                  (p) => p.id === license.product_id,
                );
                return (
                  <Box
                    key={license.id || license.product_id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <KeyIcon
                      sx={{
                        fontSize: "1.2rem",
                        color:
                          alert.severity === "error"
                            ? "error.main"
                            : "warning.main",
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ mb: 0.3 }}
                      >
                        {product?.name || "Unknown Product"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {license.expiry_date
                          ? `Expires: ${new Date(
                              license.expiry_date,
                            ).toLocaleDateString()}`
                          : "No expiry date"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              {alert.licenses.length > 3 && (
                <Box
                  sx={{
                    p: 1,
                    textAlign: "center",
                    borderRadius: 1,
                    bgcolor: "rgba(255, 255, 255, 0.03)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    ... and {alert.licenses.length - 3} more products
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Alert>
      ))}

      <Box>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            aria-label="dashboard tabs"
          >
            <Tab
              label="Forensic Tools"
              icon={<SecurityIcon />}
              iconPosition="start"
            />
            <Tab
              label={
                <Badge
                  badgeContent={
                    licenseAlerts.length > 0 ? licenseAlerts.length : 0
                  }
                  color="error"
                  invisible={licenseAlerts.length === 0}
                >
                  License Management
                </Badge>
              }
              icon={<ComputerIcon />}
              iconPosition="start"
              disabled={myProducts.length === 0}
            />
            <Tab
              label="Resources"
              icon={<ResourcesIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        {tabValue === 0 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
                mb: { xs: 3, sm: 3.5, md: 4 },
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Forensic Tools & Software
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Professional mobile forensics and investigation tools
                </Typography>
              </Box>

              <Button
                variant="outlined"
                onClick={handleViewAllProducts}
                sx={{
                  ...themedButtonStyle(),
                }}
              >
                View All Products
              </Button>
            </Box>

            {renderLatestProducts()}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: "column",
                gap: { xs: 2, sm: 0 },
                mb: { xs: 3, sm: 3.5, md: 4 },
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 0.8, sm: 1, md: 1 },
                  fontSize: { xs: "1.3rem", sm: "1.4rem", md: "1.5rem" },
                }}
              >
                Hardware & License Management
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: {
                    xs: "0.85rem",
                    sm: "0.875rem",
                    md: "0.875rem",
                  },
                  lineHeight: 1.5,
                }}
              >
                Submit and manage hardware IDs for your licensed products and
                view license status
              </Typography>

              {/* License Overview Cards */}
              {/* {myLicenses.length > 0 && (
                    <Grid container spacing={2} sx={{ mt: 3, mb: 4 }}>
                      {myLicenses
                        .filter((pl) => pl.license)
                        .map((productLicense) => {
                          const license = productLicense.license;
                          const isExpiring = isLicenseExpiring(license);
                          const isExpired = isLicenseExpired(license);
                          const daysUntilExpiry = getDaysUntilExpiry(license);

                          return (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md={4}
                              key={`${productLicense.product.id}-${license.id}`}
                            >
                              <Card
                                sx={{
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  border: 2,
                                  borderColor: isExpired
                                    ? "error.main"
                                    : isExpiring
                                    ? "warning.main"
                                    : "success.main",
                                }}
                              >
                                <CardContent
                                  sx={{
                                    pb: 2,
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: isExpired
                                          ? "error.main"
                                          : isExpiring
                                          ? "warning.main"
                                          : "success.main",
                                        width: 32,
                                        height: 32,
                                        mr: 2,
                                      }}
                                    >
                                      <KeyIcon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                      >
                                        {productLicense.product?.name ||
                                          "Unknown Product"}
                                      </Typography>
                                      <Chip
                                        label={
                                          isExpired
                                            ? "EXPIRED"
                                            : isExpiring
                                            ? `${daysUntilExpiry} days left`
                                            : "ACTIVE"
                                        }
                                        color={
                                          isExpired
                                            ? "error"
                                            : isExpiring
                                            ? "warning"
                                            : "success"
                                        }
                                        size="small"
                                      />
                                    </Box>
                                  </Box>

                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      gutterBottom
                                    >
                                      Status: {isExpired ? "Expired" : isExpiring ? "Expiring Soon" : "Active License"}
                                    </Typography>
                                  </Box>

                                  {isExpiring && !isExpired && (
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.max(
                                        0,
                                        Math.min(
                                          100,
                                          (daysUntilExpiry / 10) * 100
                                        )
                                      )}
                                      color={
                                        daysUntilExpiry <= 3
                                          ? "error"
                                          : "warning"
                                      }
                                      sx={{ mt: 2, height: 6, borderRadius: 3 }}
                                    />
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                    </Grid>
                  )} */}

              {/* Quick access button */}
              <Box sx={{ mt: { xs: 1.5, sm: 2, md: 2 } }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ComputerIcon />}
                  onClick={() => navigate("/hardware-management")}
                  sx={{
                    bgcolor: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                    fontWeight: "bold",
                    px: { xs: 2.5, sm: 3, md: 3 },
                    py: { xs: 1.2, sm: 1.5, md: 1.5 },
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1rem" },
                  }}
                >
                  Manage Hardware IDs
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<KeyIcon />}
                  onClick={() => navigate("/products")}
                  sx={{
                    ml: { xs: 1.5, sm: 2, md: 2 },
                    fontWeight: "bold",
                    px: { xs: 2.5, sm: 3, md: 3 },
                    py: { xs: 1.2, sm: 1.5, md: 1.5 },
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1rem" },
                  }}
                >
                  View All Licenses
                </Button>
              </Box>
            </Box>

            <HardwareIdSubmission products={myProducts} />
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: "column",
                gap: { xs: 2, sm: 0 },
                mb: { xs: 3, sm: 3.5, md: 4 },
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 0.8, sm: 1, md: 1 },
                  fontSize: { xs: "1.3rem", sm: "1.4rem", md: "1.5rem" },
                }}
              >
                Resources & Documentation
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  fontSize: {
                    xs: "0.85rem",
                    sm: "0.875rem",
                    md: "0.875rem",
                  },
                  lineHeight: 1.5,
                }}
              >
                Access guides, tutorials, and supplementary materials for
                forensic investigations
              </Typography>

              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                      textAlign: "center",
                      height: "100%",
                    }}
                  >
                    <PhoneIcon
                      sx={{
                        fontSize: { xs: 40, sm: 44, md: 48 },
                        color: "secondary.main",
                        mb: { xs: 1.5, sm: 1.8, md: 2 },
                      }}
                    />
                    <Typography variant="h6" gutterBottom>
                      Quick Access
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: { xs: 2, sm: 2.5, md: 3 },
                        fontSize: {
                          xs: "0.85rem",
                          sm: "0.875rem",
                          md: "0.875rem",
                        },
                        lineHeight: 1.5,
                      }}
                    >
                      Get immediate access to frequently used forensic resources
                      and tools
                    </Typography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 1.5, sm: 2, md: 2 }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => navigate("/resources")}
                      >
                        View All
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => navigate("/products")}
                      >
                        Tools
                      </Button>
                    </Stack>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default DashboardPage;
